import { ScheduledEmailSettings } from './scheduled-email-settings.model';

/**
 * Why a client's auto-scheduled emails would reach nobody. Mirrors the backend enum.
 */
export enum NoScheduledEmailRecipientReason {
  /** The client has active portal users, but every one of them has turned scheduled emails off. */
  NO_OPTED_IN_USERS = 'NO_OPTED_IN_USERS',
  /** The client has no portal users, and the client record itself has scheduled emails turned off. */
  CLIENT_OPTED_OUT = 'CLIENT_OPTED_OUT',
  /** The client has no portal users and no email address on record to fall back to. */
  NO_CLIENT_EMAIL = 'NO_CLIENT_EMAIL'
}

/**
 * Who a client's auto-scheduled emails would reach right now.
 *
 * Resolved live per request rather than read off the case. The recipients frozen into a scheduled email go
 * stale as soon as a portal user changes their notification preferences, and keeping every case in step
 * would mean fanning writes across every case that user can see.
 */
export interface ScheduledEmailRecipients {
  recipients: string[];
  /** Null when someone will receive them; otherwise says why nobody will. */
  reason?: NoScheduledEmailRecipientReason;
}

/**
 * What to tell a paralegal, per reason. One definition, because two surfaces show it — the case sidebar
 * and the court-date save form — and the whole bug was two places disagreeing about the same question.
 */
export const SCHEDULED_EMAIL_REASON_TEXT: Record<NoScheduledEmailRecipientReason, string> = {
  [NoScheduledEmailRecipientReason.NO_OPTED_IN_USERS]:
    'Every portal user at this client has turned auto-scheduled emails off. Ask them to re-enable it in ' +
    'their notification settings, or add a user who wants them.',
  [NoScheduledEmailRecipientReason.CLIENT_OPTED_OUT]:
    'This client has auto-scheduled emails turned off on their client record.',
  [NoScheduledEmailRecipientReason.NO_CLIENT_EMAIL]:
    'This client has no portal users and no email address on record to send to.'
};

/** Everything the case sidebar's Scheduled Emails panel needs, from one call. */
export interface CaseScheduledEmailPanel {
  /** Reminders configured on the case, whether or not an instance was created. */
  configured: ScheduledEmailSettings[];
  /** Instances actually created, any status, newest first. */
  instances: ScheduledEmailSettings[];
  recipients: ScheduledEmailRecipients;
}
